import React, { useId } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { TextElement } from '~/components/core/Text'
import { dimensions, spacing } from '~/constants/theme'

/** `fax` uses the new styles for list item components */
export type StyleType = 'default' | 'fax'

const selectedColor = 'var(--chakra-colors-bg-emphasized)'

const ItemWrapper = styled.div<{ $height?: string; $styleType: StyleType }>`
  position: relative;
  width: 100%;
  height: ${(props) => props.$height || '100%'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  padding-top: ${spacing.spacingMD};
  padding-right: ${(props) =>
    props.$styleType === 'fax' ? spacing.spacingLG : spacing.spacingMD};
  padding-bottom: ${spacing.spacingMD};
  padding-left: ${spacing.spacingMD};
`

const ItemButton = styled(ItemWrapper).attrs({ as: 'button', type: 'button' })`
  min-width: 0;
  margin: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;

  &:focus-visible {
    outline: none;
  }
`

const RootWrapper = styled.div<{ $selected: boolean; $styleType: StyleType }>`
  padding: ${(props) => (props.$styleType === 'fax' ? 0 : spacing.spacingMD)};
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: ${(props) =>
    props.$styleType === 'fax'
      ? dimensions.xxBorderRadiusSM
      : dimensions.borderRadiusSM};
  margin-top: ${(props) =>
    props.$styleType === 'fax' ? spacing.spacingSM : 'auto'};
  margin-right: 'auto';
  margin-bottom: ${(props) =>
    props.$styleType === 'fax' ? spacing.spacingSM : 'auto'};
  margin-left: 'auto';
  transition: background-color 0.1s ease-in-out;
  background-color: ${(props) => {
    if (props.$selected) {
      return selectedColor
    }

    if (props.$styleType === 'fax') {
      return 'inherit'
    }

    return 'var(--chakra-colors-bg-default)'
  }};
  &:hover {
    background-color: ${(props) => {
      // Don't apply hover styles to selected items
      if (props.$selected) {
        return selectedColor
      }

      return 'var(--chakra-colors-bg-muted)'
    }};
  }

  &:active {
    background-color: var(--chakra-colors-border-default);
  }

  &:has(> ${ItemButton}:focus-visible) {
    outline: 2px solid var(--chakra-colors-fg-default);
    outline-offset: -2px;
  }
`

const NewTextDot = styled.div`
  width: ${spacing.spacingLG};
  height: ${spacing.spacingLG};
  margin: ${spacing.spacingSM} 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${dimensions.borderRadiusCircle};
  cursor: pointer;
  background-color: var(--chakra-colors-brand-solid);
`

const VisuallyHiddenText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const SectionWrapper = styled.span`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TitleWrapper = styled.span<{ $shouldShowUnreadDot?: boolean | null }>`
  display: flex;
  align-items: center;
  padding-left: ${(props) =>
    props.$shouldShowUnreadDot === true || props.$shouldShowUnreadDot === null
      ? '0px'
      : `${spacing.spacingLG}`};

  > p,
  > ${TextElement} {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const DateWrapper = styled.span`
  margin-left: 10px;
  p,
  span {
    white-space: nowrap;
  }
`
interface ListItemProps {
  activated: boolean
  shouldShowUnreadDot?: boolean | null
  /** Exposes "Unread" to assistive tech; defaults to `shouldShowUnreadDot` */
  isUnread?: boolean
  title?: string | React.ReactElement
  subTitle?: string | React.ReactElement
  date?: string | React.ReactElement
  time?: string | React.ReactElement
  /** When set, the row renders as a button; leave unset when a parent link handles navigation */
  onClick?: (event: React.MouseEvent) => void
  height?: string
  styleType?: StyleType
  Thumbnail?: React.ReactElement
}
const ListItem = ({
  activated,
  title,
  subTitle,
  date,
  time,
  shouldShowUnreadDot,
  isUnread = shouldShowUnreadDot === true,
  onClick,
  height,
  styleType,
  Thumbnail,
}: ListItemProps) => {
  const idPrefix = useId()
  const titleId = `${idPrefix}-title`
  const subTitleId = `${idPrefix}-subtitle`
  const dateId = `${idPrefix}-date`
  const timeId = `${idPrefix}-time`
  const unreadId = `${idPrefix}-unread`
  const { t } = useTranslation()

  const describedBy = [
    isUnread && unreadId,
    subTitle && subTitleId,
    date && dateId,
    time && timeId,
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      {isUnread && (
        <VisuallyHiddenText id={unreadId}>
          {t('listItem.unreadStatus', 'Unread')}
        </VisuallyHiddenText>
      )}
      <SectionWrapper>
        <TitleWrapper id={titleId} $shouldShowUnreadDot={shouldShowUnreadDot}>
          {title}
        </TitleWrapper>
        <DateWrapper id={dateId}>{date}</DateWrapper>
      </SectionWrapper>
      <SectionWrapper>
        <TitleWrapper
          id={subTitleId}
          $shouldShowUnreadDot={shouldShowUnreadDot}
        >
          {subTitle}
        </TitleWrapper>
        {time && <DateWrapper id={timeId}>{time}</DateWrapper>}
      </SectionWrapper>
    </>
  )

  return (
    <RootWrapper $selected={activated} $styleType={styleType || 'default'}>
      {shouldShowUnreadDot && <NewTextDot data-testid="list-item-unread-dot" />}
      {Thumbnail}
      {onClick ? (
        <ItemButton
          $height={height}
          $styleType={styleType || 'default'}
          onClick={onClick}
          aria-current={activated ? 'true' : undefined}
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={describedBy || undefined}
        >
          {content}
        </ItemButton>
      ) : (
        <ItemWrapper $height={height} $styleType={styleType || 'default'}>
          {content}
        </ItemWrapper>
      )}
    </RootWrapper>
  )
}

export default ListItem
